import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Checkbox } from '@material-ui/core';
import {
  every, identity, isEmpty, isNull, pickBy, size,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import { media } from '../../../common/styles';
import messages from '../../messages';

const S = {
  Wrapper: styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 60px;
    background-color: ${({ theme }) => theme.palette.primary.contrastText};
  `,
  CheckboxWrapper: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 55px;
    ${media.lg`
      width: 50px;
    `}
    ${media.xl`
      width: 62px;
    `}
  `,
  Checkbox: styled(({ classes, dark, ...otherProps }) => (
    <Checkbox
      classes={{
        ...classes,
        checked: 'checked',
      }}
      {...otherProps}
    />
  ))`
    color: ${({ dark, theme }) => theme.palette.grey[dark ? 500 : 400]};
    :hover {
      color: ${({ theme }) => theme.palette.grey[500]};
    }
    &.checked {
      color: ${({ theme }) => theme.palette.primary.light};
    }
  `,
  SelectButton: styled(Button)`
    min-width: 80px;
    margin-right: 8px;
    padding: 5px 12px;
    border-color: ${({ theme }) => theme.palette.grey[400]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
  `,
  UnSelectButton: styled(Button)`
    min-width: 80px;
    margin-right: 8px;
    padding: 5px 12px;
    border-color: ${({ theme }) => theme.palette.grey[400]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
  `,
  DeleteButton: styled(Button)`
    min-width: 80px;
    margin-right: auto;
    padding: 5px 12px;
    color: ${({ theme }) => theme.palette.error.main};
    border-color: ${({ theme }) => theme.palette.error.main};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
    :focus, :hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.error.main, 0.08)};
    }
  `,
  SelectInfo: styled.p`
    color: ${({ theme }) => theme.palette.status.info};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 700;
    line-height: 1.53;
  `,
};

const TableToolBar = ({
  className, checkAll, uncheckAll, checked, checkedAll, toggleAllChecked, onOpen,
}) => (
  <S.Wrapper className={className}>
    <S.CheckboxWrapper>
      <S.Checkbox
        color="primary"
        checked={
          !isNull(checked)
            && !isEmpty(checked)
            && checkedAll(checked)
        }
        onChange={toggleAllChecked}
        dark
      />
    </S.CheckboxWrapper>
    {every(identity, checked)
      ? (
        <S.UnSelectButton type="button" variant="outlined" onClick={uncheckAll}>
          <FormattedMessage {...messages.tableToolBarUnselectButton} />
        </S.UnSelectButton>
      )
      : (
        <S.SelectButton type="button" variant="outlined" onClick={checkAll}>
          <FormattedMessage {...messages.tableToolBarSelectButton} />
        </S.SelectButton>
      )}
    <S.DeleteButton
      type="button"
      variant="outlined"
      onClick={onOpen}
    >
      <FormattedMessage {...messages.tableToolBarDeleteButton} />
    </S.DeleteButton>
    <S.SelectInfo>
      <FormattedMessage
        {...messages[size(checked) === size(pickBy(identity, checked))
          ? 'tableToolBarSelectInfoAll'
          : 'tableToolBarSelectInfo']}
        values={{ count: size(pickBy(identity, checked)) }}
      />
    </S.SelectInfo>
  </S.Wrapper>
);

TableToolBar.propTypes = {
  className: PropTypes.string,
  checkAll: PropTypes.func.isRequired,
  uncheckAll: PropTypes.func.isRequired,
  checked: PropTypes.objectOf(PropTypes.bool).isRequired,
  checkedAll: PropTypes.func.isRequired,
  toggleAllChecked: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
};

TableToolBar.defaultProps = {
  className: undefined,
};

export default TableToolBar;
